import { useEffect, useState, useCallback } from "react";
import { Handshake, Loader2, Send, CheckCircle2, FileText, PackageCheck, ArrowRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  interested: { label: "Interest sent", cls: "bg-slate-100 text-slate-500" },
  mutual: { label: "Mutual — conversation open", cls: "bg-[#DBEAFE] text-[#2563EB]" },
  quoted: { label: "Quote on the table", cls: "bg-[#FEF3C7] text-[#D97706]" },
  quote_accepted: { label: "Quote accepted", cls: "bg-[#DCFCE7] text-[#059669]" },
  converted: { label: "Deal Room opened", cls: "bg-[#DCFCE7] text-[#059669]" },
  declined: { label: "Declined", cls: "bg-slate-100 text-slate-400" },
};

function Conversation({ conn, me, onChange }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [quote, setQuote] = useState({ quantity: "", unit: "", unit_price: "", currency: "USD", incoterm: "", delivery_date: "" });
  const [cap, setCap] = useState({ quantity: "", unit: "", availability_timeframe: "", confirming_authority: "", notes: "" });
  const cid = conn.connection_id;
  const isSupplier = (conn.direction === "need" ? conn.counterpart_user_id : conn.initiator_user_id) === me;
  const active = ["mutual", "quoted", "quote_accepted", "converted"].includes(conn.status);

  const load = useCallback(() => { if (active) api.get(`/connections/${cid}/messages`).then(({ data }) => setMsgs(data.messages)); }, [cid, active]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    try { await api.post(`/connections/${cid}/messages`, { body: text }); setText(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Message blocked"); }
  };
  const proposeQuote = async () => {
    try {
      await api.post(`/connections/${cid}/quote`, {
        quantity: parseFloat(quote.quantity), unit: quote.unit, unit_price: parseFloat(quote.unit_price),
        currency: quote.currency, incoterm: quote.incoterm, delivery_date: quote.delivery_date });
      toast.success("Quote proposed"); onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const acceptQuote = async (qid) => { try { await api.post(`/connections/${cid}/quote/${qid}/accept`); toast.success("Quote accepted"); onChange(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const confirmCap = async () => {
    try {
      await api.post(`/connections/${cid}/capacity`, {
        quantity: parseFloat(cap.quantity), unit: cap.unit,
        availability_timeframe: cap.availability_timeframe, confirming_authority: cap.confirming_authority,
        notes: cap.notes });
      toast.success("Capacity confirmed"); onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const convert = async () => { try { const { data } = await api.post(`/connections/${cid}/convert`); toast.success("Deal Room opened"); onChange(); if (data.deal_id) window.location.href = `/deals?deal=${data.deal_id}&tab=predeal`; } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  if (!active) return null;
  return (
    <div className="mt-4 border-t border-slate-100 pt-4 space-y-4" data-testid={`conn-panel-${cid}`}>
      <div className="max-h-52 overflow-y-auto space-y-2 rounded-lg bg-slate-50 p-3">
        {msgs.length === 0 ? <p className="text-xs text-slate-400">No messages yet — start the secure conversation.</p> :
          msgs.map((m) => (
            <div key={m.message_id} className={`text-sm ${m.author_user_id === me ? "text-right" : ""}`}>
              <span className={`inline-block px-3 py-1.5 rounded-lg ${m.author_user_id === me ? "bg-[#2563EB] text-white" : "bg-white border border-slate-200"}`}>{m.body}</span>
              {m.translated && (
                <p className="text-[11px] text-slate-400 mt-0.5 italic">🌐 {m.translated} <span className="not-italic">· translated (original preserved)</span></p>
              )}
            </div>))}
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid={`conn-msg-input-${cid}`} placeholder="Secure message…" className="flex-1 h-10 px-3 rounded-md border border-slate-300 text-sm" />
        <button onClick={send} data-testid={`conn-msg-send-${cid}`} className="h-10 px-3 rounded-md bg-[#0A0F1C] text-white"><Send size={15} /></button>
      </div>

      {conn.quotes && conn.quotes.length > 0 && (
        <div className="space-y-1.5">
          {conn.quotes.map((q) => (
            <div key={q.quote_id} className="flex items-center justify-between text-sm rounded-lg border border-slate-200 p-2.5" data-testid={`conn-quote-${q.quote_id}`}>
              <span><FileText size={13} className="inline mr-1 text-slate-400" />{q.quantity} {q.unit} @ {q.unit_price} {q.currency} {q.incoterm && `· ${q.incoterm}`}</span>
              {q.status === "accepted" ? <span className="text-[#059669] text-xs font-semibold">Accepted</span> :
                q.proposed_by !== me && <button onClick={() => acceptQuote(q.quote_id)} data-testid={`conn-quote-accept-${q.quote_id}`} className="h-7 px-2.5 rounded bg-[#059669] text-white text-xs">Accept</button>}
            </div>))}
        </div>
      )}

      {["mutual", "quoted"].includes(conn.status) && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end rounded-lg bg-slate-50 p-3" data-testid={`conn-quote-form-${cid}`}>
          <input value={quote.quantity} onChange={(e) => setQuote({ ...quote, quantity: e.target.value })} placeholder="Qty" className="h-9 px-2 rounded border border-slate-300 text-sm" />
          <input value={quote.unit} onChange={(e) => setQuote({ ...quote, unit: e.target.value })} placeholder="Unit" className="h-9 px-2 rounded border border-slate-300 text-sm" />
          <input value={quote.unit_price} onChange={(e) => setQuote({ ...quote, unit_price: e.target.value })} placeholder="Price" className="h-9 px-2 rounded border border-slate-300 text-sm" />
          <input value={quote.incoterm} onChange={(e) => setQuote({ ...quote, incoterm: e.target.value })} placeholder="Incoterm" className="h-9 px-2 rounded border border-slate-300 text-sm" />
          <button onClick={proposeQuote} data-testid={`conn-quote-propose-${cid}`} className="h-9 rounded bg-[#0A0F1C] text-white text-xs font-medium">Propose quote</button>
        </div>
      )}

      {isSupplier && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3" data-testid={`conn-capacity-${cid}`}>
          <p className="text-xs font-semibold text-[#92400E] mb-2 flex items-center gap-1"><PackageCheck size={14} /> Confirm your capacity for this opportunity <span className="font-normal text-[#B45309]">— auditable; only your business can confirm (AI never confirms)</span></p>
          {conn.capacity_confirmation ? (
            <div className="text-xs mb-2" data-testid={`conn-capacity-status-${cid}`}>
              <span className="inline-block px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#059669] font-semibold mr-2">Business-confirmed</span>
              {conn.capacity_confirmation.quantity} {conn.capacity_confirmation.unit}
              {conn.capacity_confirmation.availability_timeframe ? ` · ${conn.capacity_confirmation.availability_timeframe}` : ""}
              {" "}by {conn.capacity_confirmation.confirming_authority}
              {conn.capacity_confirmation.notes ? <span className="block text-slate-500 mt-0.5">Notes: {conn.capacity_confirmation.notes}</span> : null}
            </div>
          ) : (
            <div className="text-xs mb-2" data-testid={`conn-capacity-status-${cid}`}>
              <span className="inline-block px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold">Not yet confirmed</span>
              <span className="text-slate-500 ml-2">Confirming real capacity strengthens your match ranking (it never overrides verification, compliance, product fit or geography).</span>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
            <input value={cap.quantity} onChange={(e) => setCap({ ...cap, quantity: e.target.value })} placeholder="Qty" className="h-9 px-2 rounded border border-slate-300 text-sm" />
            <input value={cap.unit} onChange={(e) => setCap({ ...cap, unit: e.target.value })} placeholder="Unit" className="h-9 px-2 rounded border border-slate-300 text-sm" />
            <input value={cap.availability_timeframe} onChange={(e) => setCap({ ...cap, availability_timeframe: e.target.value })} placeholder="Timeframe / frequency" className="h-9 px-2 rounded border border-slate-300 text-sm" />
            <input value={cap.confirming_authority} onChange={(e) => setCap({ ...cap, confirming_authority: e.target.value })} placeholder="Your name/role" className="h-9 px-2 rounded border border-slate-300 text-sm" />
            <button onClick={confirmCap} data-testid={`conn-capacity-confirm-${cid}`} className="h-9 rounded bg-[#D97706] text-white text-xs font-medium">Confirm</button>
            <input value={cap.notes} onChange={(e) => setCap({ ...cap, notes: e.target.value })} placeholder="Optional notes / constraints" data-testid={`conn-capacity-notes-${cid}`} className="col-span-2 sm:col-span-5 h-9 px-2 rounded border border-slate-300 text-sm" />
          </div>
        </div>
      )}

      {conn.status === "quote_accepted" && !conn.deal_id && (
        <button onClick={convert} data-testid={`conn-convert-${cid}`} className="h-10 px-5 rounded-md bg-[#059669] text-white text-sm font-medium flex items-center gap-2"><ArrowRight size={16} /> Open Deal Room</button>
      )}
      {conn.deal_id && (
        <a href={`/deals?deal=${conn.deal_id}&tab=predeal`} data-testid={`conn-open-predeal-${cid}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#059669] hover:underline">
          <CheckCircle2 size={15} /> Deal Room opened — open the Pre-Deal negotiation context <ArrowRight size={14} />
        </a>
      )}
    </div>
  );
}

export default function Connections() {
  const [conns, setConns] = useState([]);
  const [me, setMe] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => api.get("/connections").then(({ data }) => setConns(data.connections)).finally(() => setLoading(false)), []);
  useEffect(() => { api.get("/auth/me").then(({ data }) => { setMe(data.user_id); setLang(data.preferred_language || "en"); }); load(); }, [load]);

  const [lang, setLang] = useState("en");
  const LANGS = { en: "English", es: "Spanish", pt: "Portuguese", fr: "French", de: "German", zh: "Chinese", ar: "Arabic", hi: "Hindi", ja: "Japanese" };
  const changeLang = async (v) => {
    setLang(v);
    try { await api.post("/me/preferences", { preferred_language: v }); toast.success("Language updated — messages show in " + LANGS[v]); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const respond = async (cid, accept) => {
    try { await api.post(`/connections/${cid}/respond`, { accept }); toast.success(accept ? "Connected" : "Declined"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AppShell title="Connections" subtitle="Mutual interest → secure conversation → quote → Deal Room — all inside TradeMatch.">
      <div className="flex justify-end mb-4">
        <label className="flex items-center gap-2 text-xs text-slate-500">🌐 Read messages in
          <select value={lang} onChange={(e) => changeLang(e.target.value)} data-testid="language-select"
            className="h-9 px-2 rounded-md border border-slate-300 bg-white text-sm text-[#0A0F1C]">
            {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </div>
      {loading ? <div className="h-32 rounded-xl border border-slate-200 bg-white animate-pulse" /> :
        conns.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="connections-empty">
            <Handshake className="mx-auto text-slate-300" size={36} />
            <p className="mt-3 font-medium text-[#0A0F1C]">No connections yet</p>
            <p className="text-sm text-slate-500 mt-1">Express interest in a counterpart from My Needs to start.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {conns.map((c) => {
              const st = STATUS[c.status] || STATUS.interested;
              const iAmCounterpart = c.counterpart_user_id === me;
              return (
                <div key={c.connection_id} data-testid={`connection-card-${c.connection_id}`} className="rounded-xl border border-slate-200 bg-white p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-[#0A0F1C]">{c.carried?.title || "Connection"}</h3>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`connection-status-${c.connection_id}`}>{st.label}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{c.carried?.product_service} {c.carried?.quantity && `· ${c.carried.quantity}`} {c.carried?.geography && `· ${c.carried.geography}`}</p>
                    </div>
                    {c.status === "interested" && iAmCounterpart && (
                      <div className="flex gap-2">
                        <button onClick={() => respond(c.connection_id, true)} data-testid={`connection-accept-${c.connection_id}`} className="h-9 px-4 rounded-md bg-[#2563EB] text-white text-sm font-medium">Accept</button>
                        <button onClick={() => respond(c.connection_id, false)} data-testid={`connection-decline-${c.connection_id}`} className="h-9 px-4 rounded-md border border-slate-200 text-slate-500 text-sm">Decline</button>
                      </div>
                    )}
                    {c.status === "interested" && !iAmCounterpart && <span className="text-xs text-slate-400">Awaiting counterpart response…</span>}
                  </div>
                  <Conversation conn={c} me={me} onChange={load} />
                </div>
              );
            })}
          </div>
        )}
    </AppShell>
  );
}
